// src/pages/Admin/Warehouse/Dashboard.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Package, Truck, Box, Clock,
  Search, Filter, RefreshCw,
  User, ChevronDown, ChevronUp,
  Layers
} from 'lucide-react';
import { fulfillmentAPI } from '../../../api/fulfillment';
import { getTimeAgo } from '../../../utils/date';
import OrderStatusBadge from '../Orders/components/OrderStatusBadge';
import showToast from '../../../utils/toast';
import type { Shipment } from '../../../types';
import { useSearchParams } from 'react-router-dom';

interface DashboardStats {
  pending: number;
  processing: number;
  packed: number;
  shipped: number;
}

interface GroupedOrder {
  order_number: string;
  customer_name: string;
  shipments: Shipment[];
  total_items: number;
  total_packages: number;
  created_at: string;
}

const WarehouseDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [filteredShipments, setFilteredShipments] = useState<Shipment[]>([]);
  const [groupedOrders, setGroupedOrders] = useState<GroupedOrder[]>([]);
  const [singleShipments, setSingleShipments] = useState<Shipment[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats>({
    pending: filteredShipments.filter(s => s.status === 'pending').length,
    processing: filteredShipments.filter(s => s.status === 'processing').length,
    packed: filteredShipments.filter(s => s.status === 'packed').length,
    shipped: filteredShipments.filter(s => s.status === 'shipped').length,
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedWarehouse, setSelectedWarehouse] = useState<number | null>(() => {
    const param = searchParams.get('warehouse');
    return param ? Number(param) : null;
  });
  const [warehouses, setWarehouses] = useState<any[]>([{ id: null, name: 'All Warehouses' }]);
  const [showFilters, setShowFilters] = useState(false);
  const [expandedOrders, setExpandedOrders] = useState<Set<string>>(new Set());

  const statusOptions = [
    { value: 'all', label: 'All Shipments' },
    { value: 'pending', label: 'Pending' },
    { value: 'processing', label: 'Processing' },
    { value: 'packed', label: 'Packed' },
    { value: 'shipped', label: 'Shipped' }
  ];


  useEffect(() => {
    fetchDashboardData();
  }, []);

  useEffect(() => {
    console.log('Filter triggered with warehouse:', selectedWarehouse);
    filterShipments();
  }, [shipments, searchTerm, statusFilter, selectedWarehouse]);

  // Read warehouse param from URL on mount and when URL changes
  // useEffect(() => {
  //   const param = searchParams.get('warehouse');
  //   const newValue = param ? Number(param) : null;
  //   if (selectedWarehouse !== newValue) {
  //     setSelectedWarehouse(newValue);
  //   }
  // }, [searchParams]);

  // Update URL when warehouse changes
  const handleWarehouseChange = (value: string) => {
    if (value === 'all' || value === '') {
      setSelectedWarehouse(null);
      searchParams.delete('warehouse');
    } else {
      const numValue = Number(value);
      setSelectedWarehouse(numValue);
      searchParams.set('warehouse', String(numValue));
    }
    setSearchParams(searchParams);
  };

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const response = await fulfillmentAPI.getDashboardData();
      if (!response.error && response.data) {
        // Add "All Warehouses" option
        const warehouseOptions = [
          { id: null, name: 'All Warehouses' },
          ...(response.data.warehouses || [])
        ];
        setWarehouses(warehouseOptions);
        setShipments(response.data.shipments || []);
        setStats(response.data.stats || {});
      }
    } catch (error) {
      console.error('Dashboard fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchShipments = async () => {
    setLoading(true);
    try {
      const response = await fulfillmentAPI.getWarehouseShipments(selectedWarehouse || undefined);
      if (!response.error && response.data) {
        const dataWithCounts = response.data.map((shipment: any) => ({
          ...shipment,
          packages: shipment.packages?.map((pkg: any) => ({
            ...pkg,
            items_count: pkg.items?.reduce((sum: number, item: any) => sum + item.quantity, 0) || 0
          })) || []
        }));
        setShipments(dataWithCounts);
        calculateStats(dataWithCounts);
        groupAndSeparateShipments(dataWithCounts);
      }
    } catch (error) {
      console.error('Failed to fetch shipments:', error);
      showToast('Failed to load shipments', 'error');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (data: Shipment[]) => {
    setStats({
      pending: data.filter(s => s.status === 'pending').length,
      processing: data.filter(s => s.status === 'processing').length,
      packed: data.filter(s => s.status === 'packed').length,
      shipped: data.filter(s => s.status === 'shipped').length
    });
  };

  const groupAndSeparateShipments = (data: Shipment[]) => {
    // Group shipments by order
    const grouped = data.reduce((acc, shipment) => {
      const key = shipment.order_number;
      if (!acc[key]) {
        acc[key] = {
          order_number: shipment.order_number,
          customer_name: shipment.customer_name,
          shipments: [],
          total_items: 0,
          total_packages: 0,
          created_at: shipment.created_at
        };
      }
      acc[key].shipments.push(shipment);
      acc[key].total_items += shipment.items_count || 0;
      acc[key].total_packages += shipment.packages?.length || 0;
      return acc;
    }, {} as Record<string, GroupedOrder>);

    const groupedOrdersList = Object.values(grouped);

    // Separate: single shipment orders vs multi-shipment orders
    const multiShipmentOrders = groupedOrdersList.filter(g => g.shipments.length > 1);
    const singleShipmentOrders = groupedOrdersList.filter(g => g.shipments.length === 1);

    // Flatten single shipment orders into regular shipment list
    const singleShipmentsList = singleShipmentOrders.flatMap(g => g.shipments);

    setGroupedOrders(multiShipmentOrders);
    setSingleShipments(singleShipmentsList);
  };

  const filterShipments = () => {
    let filtered = [...shipments];

    if (statusFilter !== 'all') {
      filtered = filtered.filter(s => s.status === statusFilter);
    }

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(s =>
        s.order_number.toLowerCase().includes(term) ||
        s.customer_name.toLowerCase().includes(term)
      );
    }

    if (selectedWarehouse) {
      filtered = filtered.filter(s => s.warehouse === selectedWarehouse);
    }

    setFilteredShipments(filtered);

    // Update stats
    setStats({
      pending: filtered.filter(s => s.status === 'pending').length,
      processing: filtered.filter(s => s.status === 'processing').length,
      packed: filtered.filter(s => s.status === 'packed').length,
      shipped: filtered.filter(s => s.status === 'shipped').length,
    });
    groupAndSeparateShipments(filtered);
  };

  const handleViewShipment = (shipmentId: string, e: React.MouseEvent) => {
    const params = new URLSearchParams();
    if (selectedWarehouse !== null) {
      params.set('warehouse', String(selectedWarehouse));
    }
    const queryString = params.toString();
    e.stopPropagation();
    navigate(`/dashboard/warehouse/shipment/${shipmentId}${queryString ? `?${queryString}` : ''}`);
  };

  const toggleOrderExpansion = (orderNumber: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedOrders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(orderNumber)) {
        newSet.delete(orderNumber);
      } else {
        newSet.add(orderNumber);
      }
      return newSet;
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending': return <Clock className="w-5 h-5 text-yellow-500" />;
      case 'processing': return <Package className="w-5 h-5 text-blue-500" />;
      case 'packed': return <Box className="w-5 h-5 text-green-500" />;
      case 'shipped': return <Truck className="w-5 h-5 text-purple-500" />;
      default: return <Package className="w-5 h-5 text-gray-400" />;
    }
  };

  const getOrderStatus = (shipments: Shipment[]) => {
    const statuses = shipments.map(s => s.status);
    if (statuses.every(s => s === 'shipped')) return 'shipped';
    if (statuses.every(s => s === 'packed')) return 'packed';
    if (statuses.some(s => s === 'processing')) return 'processing';
    return 'pending';
  };

  const StatCard = ({ title, value, icon: Icon, color, bgColor }: any) => (
    <div className={`${bgColor} rounded-xl p-3 flex items-center gap-3`}>
      <div className={`p-2 rounded-lg ${color}`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div>
        <p className="text-xs font-medium text-gray-600">{title}</p>
        <p className="text-xl font-bold text-gray-900">{value}</p>
      </div>
    </div>
  );

  // Single Shipment Card (regular view)
  const SingleShipmentCard = ({ shipment }: { shipment: Shipment }) => {
    const totalPackages = shipment.packages?.length || 0;
    const packedPackages = shipment.packages?.filter(p => p.status === 'packed').length || 0;
    const totalPackageItems = shipment.packages?.reduce((sum, p) => sum + (p.items_count || 0), 0) || 0;

    return (
      <div
        className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
        onClick={(e) => handleViewShipment(shipment.id, e)}
      >
        <div className="p-4">
          {/* Top row */}
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              {getStatusIcon(shipment.status)}
              <span className="font-mono text-sm font-medium text-gray-900">
                #{shipment.order_number}
              </span>
            </div>
            <OrderStatusBadge status={shipment.status} size="sm" />
          </div>

          {/* Customer */}
          <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
            <User className="w-3.5 h-3.5 text-gray-400" />
            <span className="truncate">{shipment.customer_name}</span>
          </div>

          {/* Package Summary */}
          <div className="flex items-center gap-4 text-xs text-gray-500">
            <span className="flex items-center gap-1">
              <Box className="w-3.5 h-3.5" />
              {totalPackages} package{totalPackages !== 1 ? 's' : ''}
              {totalPackages > 0 && (
                <span className="text-gray-400 ml-1">
                  ({packedPackages} packed)
                </span>
              )}
            </span>
            <span className="flex items-center gap-1">
              <Package className="w-3.5 h-3.5" />
              {shipment.items_count || 0} item{shipment.items_count !== 1 ? 's' : ''}
              {totalPackageItems > 0 && (
                <span className="text-gray-400 ml-1">
                  ({totalPackageItems} in packages)
                </span>
              )}
            </span>
            <span className="text-gray-400">•</span>
            <span>{getTimeAgo(shipment.created_at)}</span>
          </div>

          {/* Package Tags */}
          {shipment.packages && shipment.packages.length > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-100 flex flex-wrap items-center gap-1.5">
              {shipment.packages.slice(0, 4).map((pkg) => (
                <span
                  key={pkg.id}
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${pkg.status === 'packed'
                    ? 'bg-green-100 text-green-700'
                    : pkg.status === 'processing'
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-100 text-gray-600'
                    }`}
                >
                  <Box className="w-3 h-3" />
                  Package #{pkg.package_number}
                  {pkg.items_count > 0 && (
                    <span className="opacity-75">({pkg.items_count})</span>
                  )}
                </span>
              ))}
              {shipment.packages.length > 4 && (
                <span className="text-xs text-gray-400">
                  +{shipment.packages.length - 4} more
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Shipment Card within Multi-Shipment Group
  const GroupedShipmentCard = ({ shipment, index, totalShipments }: { shipment: Shipment; index: number; totalShipments: number }) => {
    const totalPackages = shipment.packages?.length || 0;
    const packedPackages = shipment.packages?.filter(p => p.status === 'packed').length || 0;
    const totalPackageItems = shipment.packages?.reduce((sum, p) => sum + (p.items_count || 0), 0) || 0;

    return (
      <div className="relative pl-6 pb-4 last:pb-0">
        {/* Connecting line */}
        {index < totalShipments - 1 && (
          <div className="absolute left-3 top-4 bottom-0 w-0.5 bg-gray-200" />
        )}
        {/* Dot on the line */}
        <div className="absolute left-1 top-3 w-4 h-4 rounded-full bg-blue-500 border-2 border-white shadow-sm" />

        {/* Shipment Card */}
        <div
          className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-md transition-shadow cursor-pointer ml-4"
          onClick={(e) => handleViewShipment(shipment.id, e)}
        >
          <div className="p-3 sm:p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                {getStatusIcon(shipment.status)}
                <span className="text-xs font-medium text-gray-500">
                  Shipment {index + 1}
                </span>
                <span className="text-xs text-gray-400">•</span>
                <span className="text-xs text-gray-600">{shipment.warehouse_name}</span>
              </div>
              <OrderStatusBadge status={shipment.status} size="sm" />
            </div>

            <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
              <span className="flex items-center gap-1">
                <Box className="w-3.5 h-3.5" />
                {totalPackages} package{totalPackages !== 1 ? 's' : ''}
                {totalPackages > 0 && (
                  <span className="text-gray-400 ml-1">
                    ({packedPackages} packed)
                  </span>
                )}
              </span>
              <span className="flex items-center gap-1">
                <Package className="w-3.5 h-3.5" />
                {shipment.items_count || 0} item{shipment.items_count !== 1 ? 's' : ''}
                {totalPackageItems > 0 && (
                  <span className="text-gray-400 ml-1">
                    ({totalPackageItems} in packages)
                  </span>
                )}
              </span>
              <span className="text-gray-400">•</span>
              <span>{getTimeAgo(shipment.created_at)}</span>
            </div>

            {shipment.packages && shipment.packages.length > 0 && (
              <div className="mt-2 pt-2 border-t border-gray-100 flex flex-wrap items-center gap-1.5">
                {shipment.packages.slice(0, 3).map((pkg) => (
                  <span
                    key={pkg.id}
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${pkg.status === 'packed'
                      ? 'bg-green-100 text-green-700'
                      : pkg.status === 'processing'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-gray-100 text-gray-600'
                      }`}
                  >
                    <Box className="w-3 h-3" />
                    #{pkg.package_number}
                    {pkg.items_count > 0 && (
                      <span className="opacity-75">({pkg.items_count})</span>
                    )}
                  </span>
                ))}
                {shipment.packages.length > 3 && (
                  <span className="text-xs text-gray-400">
                    +{shipment.packages.length - 3} more
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 py-4 sm:py-6 space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Warehouse</h1>
          <p className="text-sm text-gray-500">Manage shipments and packages</p>
        </div>
        <div className="flex items-center gap-2">
          {warehouses.length > 1 && (

            <select
              value={selectedWarehouse === null ? 'all' : String(selectedWarehouse)}
              onChange={(e) => handleWarehouseChange(e.target.value)}
              className="flex-1 sm:flex-none px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Warehouses</option>
              {warehouses
                .filter(w => w.id !== null && w.id !== undefined)
                .map((wh) => (
                  <option key={wh.id} value={String(wh.id)}>
                    {wh.name}
                  </option>
                ))}
            </select>
          )}
          <button
            onClick={fetchShipments}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3">
        <StatCard
          title="Pending"
          value={stats.pending}
          icon={Clock}
          color="bg-yellow-500"
          bgColor="bg-yellow-50"
        />
        <StatCard
          title="Processing"
          value={stats.processing}
          icon={Package}
          color="bg-blue-500"
          bgColor="bg-blue-50"
        />
        <StatCard
          title="Packed"
          value={stats.packed}
          icon={Box}
          color="bg-green-500"
          bgColor="bg-green-50"
        />
        <StatCard
          title="Shipped"
          value={stats.shipped}
          icon={Truck}
          color="bg-purple-500"
          bgColor="bg-purple-50"
        />
      </div>

      {/* Search & Filter */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search orders..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center justify-center gap-2 px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <Filter className="w-4 h-4" />
          {statusFilter !== 'all' && (
            <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
          )}
        </button>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-xl border border-gray-200">
          {statusOptions.map((opt) => (
            <button
              key={opt.value}
              onClick={() => {
                setStatusFilter(opt.value);
                setShowFilters(false);
              }}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${statusFilter === opt.value
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}

      {/* Shipments List */}
      <div className="space-y-4">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : filteredShipments.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
            <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No shipments found</p>
            <p className="text-sm text-gray-400 mt-1">Try adjusting your filters</p>
          </div>
        ) : (
          <>
            {/* Single Shipments - Regular Cards */}
            {singleShipments.length > 0 && (
              <div className="space-y-3">
                {singleShipments.map((shipment) => (
                  <SingleShipmentCard key={shipment.id} shipment={shipment} />
                ))}
              </div>
            )}

            {/* Multi-Shipment Orders - Grouped Containers */}
            {groupedOrders.length > 0 && (
              <div className="space-y-4">
                {groupedOrders.map((group) => {
                  const isExpanded = expandedOrders.has(group.order_number);
                  const orderStatus = getOrderStatus(group.shipments);

                  return (
                    <div
                      key={group.order_number}
                      className="rounded-xl border border-blue-200 bg-blue-50/30 overflow-hidden transition-all"
                    >
                      {/* Order Header */}
                      <div
                        className="px-4 py-3 bg-blue-50/50 flex items-center justify-between cursor-pointer hover:bg-blue-50/70 transition-colors"
                        onClick={(e) => toggleOrderExpansion(group.order_number, e)}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-sm font-semibold text-gray-900">
                              #{group.order_number}
                            </span>
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs">
                              <Layers className="w-3 h-3" />
                              {group.shipments.length} shipments
                            </span>
                          </div>
                          <span className="text-sm text-gray-600 truncate">
                            {group.customer_name}
                          </span>
                          <OrderStatusBadge status={orderStatus} size="sm" />
                        </div>

                        <div className="flex items-center gap-3 flex-shrink-0">
                          <span className="text-xs text-gray-500 hidden sm:inline">
                            {group.total_items} items • {group.total_packages} packages
                          </span>
                          {isExpanded ? (
                            <ChevronUp className="w-4 h-4 text-gray-400" />
                          ) : (
                            <ChevronDown className="w-4 h-4 text-gray-400" />
                          )}
                        </div>
                      </div>

                      {/* Shipments */}
                      {isExpanded && (
                        <div className="px-4 py-3">
                          <div className="relative pl-2">
                            {group.shipments.map((shipment, index) => (
                              <GroupedShipmentCard
                                key={shipment.id}
                                shipment={shipment}
                                index={index}
                                totalShipments={group.shipments.length}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      {/* Results count */}
      {filteredShipments.length > 0 && (
        <div className="text-center text-sm text-gray-500 py-2">
          {filteredShipments.length} shipment{filteredShipments.length !== 1 ? 's' : ''} •
          {singleShipments.length + groupedOrders.length} order
          {singleShipments.length + groupedOrders.length !== 1 ? 's' : ''}
          {groupedOrders.length > 0 && ` (${groupedOrders.length} with multiple shipments)`}
        </div>
      )}
    </div>
  );
};

export default WarehouseDashboard;